import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const AUTH_FAILURE = 'auth';
const TRANSIENT_FAILURE = 'transient';

/**
 * Classifies a failed request so the reducer can decide whether the session
 * survives it. Only a response that rejects our credentials ends the session;
 * a network error or a server-side fault is transient, because signing users
 * out on every blip is worse than failing to load their profile once.
 */
const classifyRequestFailure = (error) => {
  const status = error?.response?.status;
  return status === 401 || status === 403 ? AUTH_FAILURE : TRANSIENT_FAILURE;
};

/**
 * Ends initialization with a classified failure. Auth failures drop the stored
 * tokens here rather than in the reducer, so the next page load starts from a
 * clean signed-out state instead of retrying a session that can never recover.
 */
const failInitialization = (rejectWithValue, reason, message) => {
  if (reason === AUTH_FAILURE) {
    localStorage.removeItem('authTokens');
  }
  return rejectWithValue({ reason, message });
};

/**
 * Thunk to initialize authentication.
 *
 * - When tokens are present in the URL (e.g. after login), simply update tokens
 *   without dispatching a logout.
 * - If saved tokens exist in localStorage, load them into state and renew them
 *   through refreshAuthToken when the access token has expired.
 * - Failures are classified before they are reported: an AUTH_FAILURE means the
 *   session is finished and both storage and state are cleared, a
 *   TRANSIENT_FAILURE leaves the tokens alone. Either way the thunk reaches a
 *   terminal state, so AuthGate always stops showing the loading spinner.
 */
export const initializeAuth = createAsyncThunk(
  'user/initializeAuth',
  async (_, { dispatch, getState, rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams(window.location.search);

      // Extract tokens from URL if present.
      const urlAccessToken = queryParams.get('accessToken');
      const urlIdToken = queryParams.get('idToken');
      const urlRefreshToken = queryParams.get('refreshToken');

      if (urlAccessToken && urlIdToken && urlRefreshToken) {
        // Instead of logging out, update the tokens.
        dispatch(
          setTokens({
            accessToken: urlAccessToken,
            idToken: urlIdToken,
            refreshToken: urlRefreshToken,
          })
        );
        localStorage.setItem(
          'authTokens',
          JSON.stringify({
            accessToken: urlAccessToken,
            idToken: urlIdToken,
            refreshToken: urlRefreshToken,
          })
        );

        // Remove tokens from URL so they are not visible.
        queryParams.delete('accessToken');
        queryParams.delete('idToken');
        queryParams.delete('refreshToken');
        queryParams.delete('expiresIn');
        const newQueryString = queryParams.toString();
        const newUrl =
          window.location.pathname +
          (newQueryString ? `?${newQueryString}` : '') +
          window.location.hash;
        window.history.replaceState({}, document.title, newUrl);
      }

      // If no tokens in URL, check for saved tokens.
      const savedTokens = JSON.parse(localStorage.getItem('authTokens') || '{}');
      if (!savedTokens.accessToken) {
        // No tokens exist; nothing more to do.
        return;
      }

      // If tokens are not already in Redux state, load them.
      const state = getState();
      if (!state.user.tokens.accessToken) {
        dispatch(setTokens(savedTokens));
      }

      // Validate saved tokens. An undecodable token counts as expired so the
      // refresh path — not the outer catch — decides what happens to it.
      const isTokenExpired = (token) => {
        if (!token) return true;
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const currentTime = Math.floor(Date.now() / 1000);
          return payload.exp < currentTime;
        } catch (error) {
          return true;
        }
      };

      if (isTokenExpired(savedTokens.accessToken)) {
        try {
          await dispatch(refreshAuthToken()).unwrap();
        } catch (error) {
          return failInitialization(
            rejectWithValue,
            error?.reason === TRANSIENT_FAILURE ? TRANSIENT_FAILURE : AUTH_FAILURE,
            `Token refresh failed: ${error?.message || error}`
          );
        }
      }

      // Fetch additional user info.
      try {
        await dispatch(fetchUserInfo()).unwrap();
        await dispatch(fetchUserRegistrations()).unwrap();
      } catch (error) {
        return failInitialization(
          rejectWithValue,
          error?.reason === AUTH_FAILURE ? AUTH_FAILURE : TRANSIENT_FAILURE,
          error?.message || String(error)
        );
      }

      return;
    } catch (error) {
      // Unexpected failure rather than a rejected session: keep the tokens.
      return failInitialization(rejectWithValue, TRANSIENT_FAILURE, error.message);
    }
  }
);

/**
 * Thunk to fetch user info.
 *
 * After fetching, compares the access token's "sub" to the fetched user’s ID.
 * Rejections carry a { reason, message } payload so initializeAuth can tell a
 * rejected session apart from a request that merely failed.
 */
export const fetchUserInfo = createAsyncThunk(
  'user/fetchUserInfo',
  async (_, { getState, dispatch, rejectWithValue }) => {
    const { accessToken } = getState().user.tokens;
    try {
      // Cache-busting parameter.
      const proxyResponse = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/app/proxy/userinfo`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: { cb: Date.now() },
        }
      );

      if (!proxyResponse.data) {
        return rejectWithValue({
          reason: TRANSIENT_FAILURE,
          message: 'No proxy user info.',
        });
      }

      const fusionAuthUserId = proxyResponse.data.sub;
      const apiKey = process.env.REACT_APP_SERVER_API_KEY;
      const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}/app/me/userinfo`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'x-api-key': apiKey,
          },
          params: { fusionAuthUserId, cb: Date.now() },
        }
      );

      if (!response.data) {
        return rejectWithValue({
          reason: TRANSIENT_FAILURE,
          message: 'No user info from DB.',
        });
      }

      const { user, organizations } = response.data;
      if (!user.email) {
        return rejectWithValue({
          reason: TRANSIENT_FAILURE,
          message: 'User email missing.',
        });
      }

      // Decode the access token and compare its "sub" with the fetched user's ID.
      const tokenPayload = JSON.parse(atob(accessToken.split('.')[1]));
      if (user.fusionauth_user_id !== tokenPayload.sub) {
        return rejectWithValue({
          reason: AUTH_FAILURE,
          message: 'Stale session detected. Please log in again.',
        });
      }

      const appRoles = user.roles.reduce((acc, role, index) => {
        acc[user.applications[index]] = role;
        return acc;
      }, {});

      dispatch(setUser(user));
      dispatch(updateUserAddresses(user.addresses || []));
      dispatch(setOrganizations(organizations));
      dispatch(
        updateOrgAddresses(
          organizations.flatMap((org) => org.addresses || [])
        )
      );
      dispatch(setNotifications(user.notifications || []));
      dispatch(setAppRoles(appRoles));

      return;
    } catch (error) {
      return rejectWithValue({
        reason: classifyRequestFailure(error),
        message: error.message,
      });
    }
  }
);

/**
 * Thunk to refresh the authentication token.
 *
 * Exchanges the refresh token for a new access token through
 * /app/proxy/revalidate, the only route that performs this exchange against
 * FusionAuth. Rejections carry a { reason, message } payload.
 */
export const refreshAuthToken = createAsyncThunk(
  'user/refreshAuthToken',
  async (_, { getState, dispatch, rejectWithValue }) => {
    const { idToken, refreshToken } = getState().user.tokens;
    if (!refreshToken) {
      return rejectWithValue({
        reason: AUTH_FAILURE,
        message: 'No refresh token available.',
      });
    }
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/app/proxy/revalidate`,
        null,
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'x-api-key': process.env.REACT_APP_SERVER_API_KEY,
          },
          params: { refresh_token: refreshToken },
        }
      );

      // The response is snake_case and carries no id_token. The existing one is
      // kept because logoutUser hands it to /app/logout, which forwards it to
      // FusionAuth as the id_token_hint — dropping it would quietly downgrade
      // logout to a local-only sign-out.
      const { access_token: accessToken, refresh_token: newRefreshToken } =
        response.data || {};
      if (!accessToken || !newRefreshToken) {
        return rejectWithValue({
          reason: AUTH_FAILURE,
          message: 'Token revalidation returned no tokens.',
        });
      }

      const newTokens = { accessToken, idToken, refreshToken: newRefreshToken };
      dispatch(setTokens(newTokens));
      localStorage.setItem('authTokens', JSON.stringify(newTokens));

      return accessToken;
    } catch (error) {
      // /app/proxy/revalidate collapses every FusionAuth failure into a 503, so
      // the status cannot separate a spent refresh token from an outage. Any
      // answer at all means the server declined to renew the session; only a
      // request that never got one is treated as transient.
      return rejectWithValue({
        reason: error.response ? AUTH_FAILURE : TRANSIENT_FAILURE,
        message: error.message,
      });
    }
  }
);

/**
 * Thunk to fetch user registrations.
 */
export const fetchUserRegistrations = createAsyncThunk(
  'user/fetchUserRegistrations',
  async (_, { getState, dispatch, rejectWithValue }) => {
    const { accessToken } = getState().user.tokens;
    const userId = getState().user.user.fusionauth_user_id;
    if (!userId) {
      return rejectWithValue('No user ID.');
    }
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/sales/user/${userId}/registrations`,
        {
          headers: {
            'x-api-key': process.env.REACT_APP_SERVER_API_KEY,
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.data) {
        dispatch(setUserRegistrations(response.data));
        return response.data;
      } else {
        return rejectWithValue('No registrations data.');
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Thunk to update user preferences.
 */
export const updateUserPreferencesThunk = createAsyncThunk(
  'user/updateUserPreferences',
  async (preferences, { getState, dispatch, rejectWithValue }) => {
    const { accessToken } = getState().user.tokens;
    const userId = getState().user.user.fusionauth_user_id;
    if (!userId) {
      return rejectWithValue('No user ID.');
    }
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_SERVER_URL}/preferences/user/${userId}/update`,
        preferences,
        {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': process.env.REACT_APP_SERVER_API_KEY,
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue('Failed to update preferences.');
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Thunk to log out the user.
 *
 * When a user explicitly clicks logout, we:
 * - Set a forceLogout flag.
 * - Clear Redux state and storage.
 * - Retrieve the idToken and hit the logout route.
 */
export const logoutUser = createAsyncThunk(
  'user/logoutUser',
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      // Set a flag so subsequent page loads ignore any tokens.
      localStorage.setItem('forceLogout', 'true');
      // Clear Redux state.
      dispatch(logout());
      // Remove tokens from localStorage.
      localStorage.removeItem('authTokens');
      // Clear out all session storage.
      sessionStorage.clear();

      // Retrieve the idToken from state (if available).
      const { idToken } = getState().user.tokens;
      // Hit the logout route, passing current origin so the backend redirects back here.
      const logoutUrl = `${process.env.REACT_APP_SERVER_URL}/app/logout?id_token=${encodeURIComponent(
        idToken || ''
      )}&post_logout_redirect_uri=${encodeURIComponent(window.location.origin)}`;
      window.location.replace(logoutUrl);
      return;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Thunk to register for a sale.
 */
export const registerForSaleThunk = createAsyncThunk(
  'user/registerForSale',
  async ({ saleId, auction }, { getState, dispatch, rejectWithValue }) => {
    const { accessToken } = getState().user.tokens;
    const userId = getState().user.user.fusionauth_user_id;
    if (!userId) {
      return rejectWithValue('No user ID.');
    }
    try {
      const [year, m, d] = auction.auctionDate.split('T')[0].split('-');
      const reqBody = {
        userId,
        saleId,
        sale_date: `${year}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`,
        state: auction.state,
        county: auction.county,
        property: {
          ...auction,
          auctionDetails: {
            date: auction.auctionDate,
            time: auction.auctionDetail?.time,
            location: auction.auctionDetail?.location,
            auctionStyle: auction.auctionDetail?.type,
            saleStatusDescription: auction.auctionDetail?.status,
          },
        },
      };
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/sales/register`,
        reqBody,
        {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': process.env.REACT_APP_SERVER_API_KEY,
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.status === 200) {
        dispatch(fetchUserRegistrations());
        return response.data;
      } else {
        return rejectWithValue('Failed to register for sale.');
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Thunk to unregister from a sale.
 */
export const unregisterFromSaleThunk = createAsyncThunk(
  'user/unregisterFromSale',
  async (saleId, { getState, dispatch, rejectWithValue }) => {
    const { accessToken } = getState().user.tokens;
    const userId = getState().user.user.fusionauth_user_id;
    if (!userId) {
      return rejectWithValue('No user ID.');
    }
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_SERVER_URL}/sales/user/${userId}/sales/${saleId}/unregister`,
        {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': process.env.REACT_APP_SERVER_API_KEY,
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      if (response.status === 200) {
        dispatch(fetchUserRegistrations());
        return response.data;
      } else {
        return rejectWithValue('Failed to unregister from sale.');
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  user: {},
  organizations: [],
  userAddresses: [],
  orgAddresses: [],
  notifications: [],
  notificationsUpdated: false,
  tokens: { accessToken: '', idToken: '', refreshToken: '' },
  profileUpdated: false,
  appRoles: {},
  userRegistrations: [],
  userRoles: [],
  userApplications: [],
  authInitialized: false,
};

const clearAuthState = (state) => {
  state.user = {};
  state.organizations = [];
  state.userAddresses = [];
  state.orgAddresses = [];
  state.notifications = [];
  state.tokens = { accessToken: '', idToken: '', refreshToken: '' };
  state.profileUpdated = false;
  state.appRoles = {};
  state.userRegistrations = [];
  state.userRoles = [];
  state.userApplications = [];
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    toggleNotificationsUpdated: (state) => {
      state.notificationsUpdated = !state.notificationsUpdated;
    },
    setUser: (state, action) => {
      state.user = action.payload || {};
    },
    setOrganizations: (state, action) => {
      state.organizations = action.payload || [];
    },
    updateUser: (state, action) => {
      state.user = { ...state.user, ...action.payload };
    },
    updateOrganization: (state, action) => {
      const orgIndex = state.organizations.findIndex(
        (org) => org.id === action.payload.id
      );
      if (orgIndex !== -1) {
        state.organizations[orgIndex] = {
          ...state.organizations[orgIndex],
          ...action.payload,
        };
      }
    },
    updateUserAddresses: (state, action) => {
      state.userAddresses = action.payload || [];
    },
    updateOrgAddresses: (state, action) => {
      state.orgAddresses = action.payload || [];
    },
    setNotifications: (state, action) => {
      state.notifications = action.payload || [];
    },
    markAllNotificationsRead: (state) => {
      state.notifications.forEach((notification) => {
        notification.status = 'read';
      });
    },
    setUserRegistrations: (state, action) => {
      state.userRegistrations = action.payload || [];
    },
    addOrganization: (state, action) => {
      state.organizations.push(action.payload);
    },
    deleteOrganization: (state, action) => {
      state.organizations = state.organizations.filter(
        (org) => org.id !== action.payload
      );
    },
    addAddress: (state, action) => {
      state.userAddresses.push(action.payload);
    },
    deleteAddress: (state, action) => {
      state.userAddresses = state.userAddresses.filter(
        (addr) => addr.id !== action.payload
      );
    },
    deleteOrganizationAddress: (state, action) => {
      const org = state.organizations.find((org) => org.id === action.payload.orgId);
      if (org) {
        org.addresses = org.addresses.filter(
          (addr) => addr.id !== action.payload.addrId
        );
      }
    },
    setTokens: (state, action) => {
      state.tokens = action.payload;
    },
    setProfileUpdated: (state, action) => {
      state.profileUpdated = action.payload;
    },
    setUserRoles: (state, action) => {
      state.userRoles = action.payload || [];
    },
    setUserApplications: (state, action) => {
      state.userApplications = action.payload || [];
    },
    setAppRoles: (state, action) => {
      state.appRoles = action.payload || {};
    },
    logout: (state) => {
      clearAuthState(state);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initializeAuth.fulfilled, (state) => {
        state.authInitialized = true;
      })
      .addCase(initializeAuth.rejected, (state, action) => {
        // Initialization is over either way: AuthGate keys off authInitialized,
        // so leaving it false is what strands the page on the spinner.
        state.authInitialized = true;
        if (action.payload?.reason === AUTH_FAILURE) {
          clearAuthState(state);
        }
      })
      .addCase(fetchUserInfo.fulfilled, (state) => {})
      .addCase(fetchUserInfo.rejected, (state, action) => {})
      .addCase(refreshAuthToken.fulfilled, (state) => {})
      .addCase(refreshAuthToken.rejected, (state, action) => {})
      .addCase(fetchUserRegistrations.fulfilled, (state) => {})
      .addCase(fetchUserRegistrations.rejected, (state, action) => {})
      .addCase(updateUserPreferencesThunk.fulfilled, (state) => {})
      .addCase(updateUserPreferencesThunk.rejected, (state, action) => {})
      .addCase(logoutUser.fulfilled, (state) => {})
      .addCase(logoutUser.rejected, (state, action) => {})
      .addCase(registerForSaleThunk.fulfilled, (state) => {})
      .addCase(registerForSaleThunk.rejected, (state, action) => {})
      .addCase(unregisterFromSaleThunk.fulfilled, (state) => {})
      .addCase(unregisterFromSaleThunk.rejected, (state, action) => {});
  },
});

export const {
  toggleNotificationsUpdated,
  setUser,
  setOrganizations,
  updateUser,
  updateOrganization,
  updateUserAddresses,
  updateOrgAddresses,
  setNotifications,
  markAllNotificationsRead,
  setUserRegistrations,
  addOrganization,
  deleteOrganization,
  addAddress,
  deleteAddress,
  deleteOrganizationAddress,
  setTokens,
  setProfileUpdated,
  setUserRoles,
  setUserApplications,
  setAppRoles,
  logout,
} = userSlice.actions;

export const selectUserInfo = (state) => state.user;
export const selectNotifications = (state) => state.user.notifications;
export const selectTokens = (state) => state.user.tokens;
export const selectProfileUpdated = (state) => state.user.profileUpdated;
export const selectUserApplications = (state) => state.user.userApplications;
export const selectUserRoles = (state) => state.user.userRoles;
export const selectAppRoles = (state) => state.user.appRoles;
export const selectNotificationsUpdated = (state) => state.user.notificationsUpdated;
export const selectAuthInitialized = (state) => state.user.authInitialized;

export default userSlice.reducer;


export const setupStorageListener = (dispatch) => {
  const storageListener = (event) => {
    if (event.key === 'authTokens' && event.newValue === null) {
      dispatch(logout());
    }
  };

  window.addEventListener('storage', storageListener);

  return () => {
    window.removeEventListener('storage', storageListener);
  };
};
